import {useEffect, useRef} from "react";
import {Col} from "../../../components/Flex/Flex";
import style from '../../../style/Map.module.css';
import {useSystems} from "../../../utils/api";
import {UserLocation, getPos, scanDirs, colors, TAN} from "../../../utils/MapUtils.tsx";

export default function TrackingMap({scans, locations, sighting, showCurrentLines, currentLocation}) {
    const ref = useRef(null);


    return (
        <Col ref={ref} grow={100} shrink={1} className={style.wrapper}>
            <MapCanvas scans={scans} current={currentLocation} showCurrentLines={showCurrentLines}/>
            <div className={style.legend}>
                <div>Scan Location: <div className={style.dotRed}/></div>
                <div>Possible Location: <div className={style.dotBlue}/></div>
                <div>Last Sighting: <div className={style.dotYellow}/></div>
                {currentLocation ? <div>Current location: <div className={style.dotPurple} /></div> : null}
            </div>
            <div className={style.dotWrapper}>
                {scans.map(s => <ScanLocation key={s.id} scan={s}/>)}
                {locations.map(l => <TargetLocation key={l.id} location={l}/>)}
                {sighting ? <SightingLocation sight={sighting}/> : null}
                {currentLocation ? <UserLocation location={currentLocation} /> : null}
            </div>
        </Col>
    )
}


const ScanLocation = ({scan}) => {
    const {systems} = useSystems();
    return <ScanDot scan={scan} systems={systems} />
}

const ScanDot = ({scan, systems}) => {
    let name = 'Loading...';
    if (scan.system) {
        let sys = systems.find(s => s.id === scan.system);
        if (sys) {
            name = sys.name;
        }
    } else {
        name = 'Deep Space';
    }
    let title= `${name} (${scan.x}, ${scan.y})`;
    return <div className={style.system} style={{bottom: getPos(scan.y), left: getPos(scan.x)}} title={title}>
    <div className={style.dotRed}/>
    </div>
}
const TargetLocation = ({location}) => {
    return <div className={style.system} style={{bottom: getPos(location.galY), left: getPos(location.galX)}} title={`${location.name} (${location.galX}, ${location.galY})`}>
        <div className={style.dotBlue}/>
    </div>
}
const SightingLocation = ({sight}) => {
    let system = sight.location.system;
    return <div className={style.system} style={{bottom: getPos(system.galY), left: getPos(system.galX)}} title={`${system.name} (${system.galX}, ${system.galY})`} ><div className={style.dotYellow} /></div>;
}



const distances = {
    'Short': [1, 33],
    'Medium': [33, 65],
    'Long': [65, 99],
    'Very Distant': [100, 1000],
    'Unknown': [0, 1000],
}
const distanceLines = [33, 65, 100];



const drawMap = (canvas, scans, location, scale=1) => {
    const context = canvas.getContext('2d');
    context.fillStyle = colors.fill;
    context.strokeStyle = colors.strokeFound;
    context.lineWidth = 2;

    for (let scan of scans) {

        if(scan.distance === 'Lock') {
            continue;
        }

        let range = distances[scan.distance];
        if(!range) {
            continue;
        }

        let x0 = (scan.x + 500)*scale;
        let y0 = (-scan.y + 500)*scale;
        //start

        let [dmin, dmax] = range;
        dmin *= scale;
        dmax *= scale;

        let dirs = getPoints(scan.direction);

        if (dmin > 0) {
            context.beginPath();
            context.strokeStyle = colors.strokeHelp;
            context.moveTo(x0, y0);
            context.lineTo(x0+(dmin*dirs[0][0]), y0+(dmin*dirs[0][1]));
            context.moveTo(x0, y0);
            context.lineTo(x0+(dmin*dirs[2][0]), y0+(dmin*dirs[2][1]));
            context.stroke();
        }

        context.beginPath();
        context.strokeStyle = colors.strokeFound;
        context.moveTo(x0+(dmin*dirs[0][0]), y0+(dmin*dirs[0][1]));
        for(let d of dirs) {
            context.lineTo(x0+(dmin*d[0]), y0+(dmin*d[1]));
        }
        dirs.reverse();
        for(let d of dirs) {
            context.lineTo(x0+(dmax*d[0]), y0+(dmax*d[1]));
        }

        context.closePath();
        context.stroke();
        context.fill();

    }

    if(location) {
        let x = location.coordinates.galaxy.attributes.x+500;
        let y = -location.coordinates.galaxy.attributes.y+500;
        context.strokeStyle = colors.strokeScan;
        for(let [mx, my] of scanDirs) {
            context.beginPath();
            context.moveTo(x,y);
            context.lineTo(x+(1000*mx), y+(1000*my));
            context.stroke();
        }

        context.setLineDash([5, 5]);
        for(let d of distanceLines) {
            context.beginPath();
            context.rect(x - d, y - d, d * 2, d * 2);
            context.stroke();
        }
        context.setLineDash([]);

    }


    return () => {
        context.clearRect(0, 0, canvas.width, canvas.height);
    }
}
const MapCanvas = ({scans, current, showCurrentLines}) => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas) {
            return drawMap(canvas, scans, showCurrentLines ? current : null);
        }
    }, [scans, current, showCurrentLines]);


    return (
        <canvas className={style.canvas} ref={canvasRef} width={1000} height={1000}></canvas>
    )
}



const getPoints = (direction) => {
    switch (direction) {
        case 'N':
            return [
                [TAN, -1],
                [0, -1],
                [-TAN, -1],
            ];
        case 'NE':
            return [
                [1, -TAN],
                [1, -1],
                [TAN, -1],
            ];
        case 'E':
            return [
                [1, TAN],
                [1, 0],
                [1, -TAN]
            ];
        case 'SE':
            return [
                [TAN, 1],
                [1, 1],
                [1, TAN]
            ]
        case 'S':
            return [
                [-TAN, 1],
                [0, 1],
                [TAN, 1]
            ]
        case 'SW':
            return [
                [-1, TAN],
                [-1, 1],
                [-TAN, 1]
            ];
        case 'W':
            return [
                [-1, -TAN],
                [-1, 0],
                [-1, TAN]
            ];
        case 'NW':
            return [
                [-TAN, -1],
                [-1, -1],
                [-1, -TAN]
            ]
    }

}
